import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import LabelField from "../Form/FormFields/LabelField";
import Accordion from "../ui/Accordion";
import { lineChartConfigGenerator } from "./ChartConfigGenerator";
import { useDispatch, useSelector } from "react-redux";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import {
  setSelectedDistrictCode,
  setSelectedRegionCode,
} from "../filter/districtDetailsFilter/store";
import { useNavigate } from "react-router-dom";
import NoteComponent from "../ui/table/Notes";
import { RootState } from "../../utils/store";

const DynamicChart: React.FC<any> = ({ chartListData, width }) => {
  const selector = useSelector((state) => state);
  const selectCompontData =
    selector.chartListDetails?.componentData?.ALL?.[chartListData?.id];
  const dispatch = useDispatch();

  const selectorData = useSelector((state: RootState) => state.dynamicData);
  // Check if chart_value exists in props (pre-loaded data)
  const hasChartValue =
    chartListData?.chart_value &&
    chartListData.chart_value.data &&
    chartListData.chart_value.label_name;
  const notes = chartListData?.note;
  const noteData = chartListData?.remarks;
  // Use chart_value from props if available, otherwise use Redux selector data
  const chartData = hasChartValue
    ? chartListData.chart_value
    : selectorData?.[chartListData?.id];

  // Only show loading when fetching from API (no chart_value in props)
  const isLoading =
    !hasChartValue && selectorData.loaders[`report/chart/${chartListData?.id}`];

  const isChartReady =
    chartData &&
    Array.isArray(chartData?.label_name) &&
    chartData?.label_name?.length > 0 &&
    Array.isArray(chartData?.data) &&
    chartData?.data?.length > 0;

  const {
    series,
    categories,
    chartType,
    yAxisTitle,
    xAxisTitle,
    colors,
    format,
  } = useMemo(() => {
    if (!isChartReady) return { series: [], categories: [] };
    return lineChartConfigGenerator(chartData.data, chartData.label_name);
  }, [isChartReady, chartData]);

  // const { series, categories, chartType, yAxisTitle, xAxisTitle, colors , format } =
  //   lineChartConfigGenerator(chartData1, chartConfig1);

  const formatValue = (val: number) =>
    format === "percentage" ? `${val.toFixed(2)}%` : val.toLocaleString();
  const navigate = useNavigate();

  const chartOptions: any = {
    chart: {
      type: chartType,
      events: {
        click: (event, chartContext, config) => {
          const selectedIndex = config.dataPointIndex;
          const selectedDistrict = chartData?.data?.[selectedIndex];
          hasNavigate(selectedDistrict?.x1, selectedDistrict?.x2);
        },
      },
      stacked: chartType === "bar" || chartType === "column",
      toolbar: { show: true, tools: { download: false } },
      animations: { enabled: true, easing: "easeinout", speed: 600 },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      // dashArray: series.map((s) => s.style),
      dashArray: series.map((s) => s.style),
    },

    markers: {
      size: 5,
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 7 },
    },
    xaxis: {
      categories,
      title: { text: xAxisTitle },
      labels: {
        rotate: -45, // vertical
        rotateAlways: true, // ensures rotation even if labels fit
        style: { fontSize: "12px", colors: "#333" },
      },
      tooltip: { enabled: false }, // optional: avoid overlap tooltip
    },
    yaxis: {
      title: { text: yAxisTitle },
      labels: { formatter: (val: number) => formatValue(val) },
    },
    tooltip: {
      shared: true,
      y: { formatter: (val: number) => formatValue(val) },
    },
    legend: {
      position: "bottom",
      markers: { radius: 12 },
    },
    colors,
  };

  const hasNavigate = async (regionId: string, districtId: string) => {
    if (regionId) {
      await dispatch(setSelectedRegionCode([regionId]));
    }
    if (districtId) {
      await dispatch(setSelectedDistrictCode([districtId]));
    }
    if (selectCompontData?.has_navigate) {
      navigate(`/chart/${selectCompontData?.navigate_chart_id}`);
    }
  };

  return (
    <div className={`grid gap-2 ${width && width} `}>
      <div className="bg-white p-4 rounded-lg w-full">
        <h2 className="text-lg font-semibold mb-2">
          <div className="flex justify-between items-center mb-1">
            <div className="text-[#464E5F] text-[14px] font-semibold flex items-center">
              <LabelField label={chartListData?.title || "Dynamic Chart"} />
              {notes && (
                <NoteComponent
                  notes={noteData}
                  modalTitle={chartListData?.title}
                />
              )}
            </div>
          </div>
        </h2>

        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : (
          <div>
            <div className="w-full items-center mb-3" style={{ height: 335 }}>
              <ReactApexChart
                options={chartOptions}
                series={series}
                type={chartType}
                height="100%"
              />
            </div>

            {!chartListData.chart_value && (
              <Accordion
                title="AI Insights"
                componentID={chartListData?.id || "static"}
                chartData={chartListData}
                chartValue={chartData}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicChart;
